(() => {
  const controllers = new Map();
  const clamp = (value) => Math.max(0, Math.min(1, value));

  function init(home) {
    if (controllers.has(home)) return;
    const cleanups = [];
    const listen = (target, type, callback, options) => {
      target.addEventListener(type, callback, options);
      cleanups.push(() => target.removeEventListener(type, callback, options));
    };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const wideScreen = window.matchMedia('(min-width: 721px)');
    const enabled = home.dataset.homeMotionEnabled === 'true' && !window.Shopify?.designMode;
    const nav = home.querySelector('.home-scroll-nav');
    const toggle = home.querySelector('[data-home-motion-toggle]');
    const header = document.querySelector('.site-header');
    const chapters = [...home.querySelectorAll('[data-home-chapter]')].map((link) => ({
      link, target: home.querySelector(link.hash), bar: link.querySelector('i')
    })).filter((chapter) => chapter.target);
    const hero = home.querySelector('[data-hero]');
    const field = home.querySelector('.field__media');
    const depth = [
      { surface: hero, image: hero?.querySelector('.hero__backdrop'), kind: 'hero' },
      { surface: field, image: field?.querySelector('img'), kind: 'field' }
    ].filter((item) => item.surface && item.image);
    const revealItems = [...home.querySelectorAll('[data-reveal]')].filter((item) =>
      !item.matches('.hero__copy,.product-rail,.quick-add__content') && !item.querySelector('form')
    );
    home.querySelectorAll('[data-stagger]:not(.product-rail)').forEach((group) => {
      [...group.children].forEach((item, index) => {
        item.style.setProperty('--home-reveal-delay', `${Math.min(index, 3) * 45}ms`);
        revealItems.push(item);
      });
    });
    const loops = [...home.querySelectorAll('.hero,.ticker,.machine,.performance')];
    let paused = false;
    let keyboard = false;
    let frame = 0;
    let headerHeight = 84;
    let navHeight = 58;
    let destroyed = false;
    let swapTimer = 0;
    let previewTimer = 0;
    const motionOn = () => enabled && !reduceMotion.matches && !paused;
    const finishReveal = (item) => item.classList.remove('is-awaiting', 'is-revealing');
    const showEverything = () => revealItems.forEach(finishReveal);

    function update() {
      frame = 0;
      if (destroyed || document.hidden) return;
      const line = headerHeight + navHeight + 24;
      const positions = chapters.map(({ target }) => target.getBoundingClientRect().top);
      const end = home.getBoundingClientRect().bottom;
      let active = -1;
      positions.forEach((top, index) => { if (top <= line) active = index; });
      const visuals = depth.map((item) => ({ ...item, bounds: item.surface.getBoundingClientRect() }));
      chapters.forEach(({ link, bar }, index) => {
        if (index === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
        const distance = (positions[index + 1] ?? end) - positions[index];
        const progress = clamp((line - positions[index]) / Math.max(distance, 1));
        bar.style.transform = `scaleX(${progress.toFixed(4)})`;
      });
      visuals.forEach(({ image, bounds, kind }) => {
        if (!motionOn() || keyboard || !wideScreen.matches) {
          image.style.removeProperty('transform');
          return;
        }
        if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;
        const progress = kind === 'hero'
          ? clamp(-bounds.top / Math.max(bounds.height, 1))
          : clamp((window.innerHeight - bounds.top) / (window.innerHeight + bounds.height));
        const drift = kind === 'hero' ? progress * 42 : (progress - .5) * 36;
        image.style.transform = `translate3d(0, ${drift.toFixed(2)}px, 0) scale(${kind === 'hero' ? 1.08 : 1.07})`;
      });
    }

    function schedule() {
      if (!frame && !destroyed && !document.hidden) frame = requestAnimationFrame(update);
    }

    function measure() {
      headerHeight = Math.ceil(header?.getBoundingClientRect().height || 0);
      navHeight = Math.ceil(nav?.getBoundingClientRect().height || 0);
      home.style.setProperty('--home-header-height', `${headerHeight}px`);
      schedule();
    }

    function syncMotion() {
      home.dataset.homeMotion = motionOn() ? 'on' : 'paused';
      if (toggle) {
        toggle.hidden = !enabled || reduceMotion.matches;
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', paused ? toggle.dataset.resumeLabel : toggle.dataset.pauseLabel);
      }
      if (!motionOn()) showEverything();
      schedule();
    }

    revealItems.forEach((item) => item.classList.add('home-reveal'));
    home.toggleAttribute('data-home-idle', document.hidden);
    syncMotion();
    if ('IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) return;
          if (motionOn() && !keyboard && target.classList.contains('is-awaiting')) {
            target.classList.add('is-revealing');
            target.classList.remove('is-awaiting');
          } else finishReveal(target);
          observer.unobserve(target);
        });
      }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });
      revealItems.forEach((item) => {
        if (motionOn() && item.getBoundingClientRect().top >= window.innerHeight) {
          item.classList.add('is-awaiting');
          revealObserver.observe(item);
        }
      });
      const visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => target.classList.toggle('home-in-view', isIntersecting));
      });
      loops.forEach((item) => visibilityObserver.observe(item));
      cleanups.push(() => revealObserver.disconnect(), () => visibilityObserver.disconnect());
    }
    listen(home, 'transitionend', (event) => {
      if (event.propertyName === 'transform' && event.target.classList.contains('home-reveal')) {
        event.target.classList.remove('is-revealing');
      }
    });
    listen(home, 'focusin', (event) => {
      const pending = event.target.closest('.home-reveal');
      if (pending) finishReveal(pending);
    });
    listen(document, 'keydown', (event) => {
      if (!['Tab', 'PageDown', 'PageUp', 'Home', 'End', 'ArrowDown', 'ArrowUp', ' '].includes(event.key)) return;
      keyboard = true;
      home.dataset.homeInput = 'keyboard';
      showEverything();
      schedule();
    });
    const pointerInput = () => {
      keyboard = false;
      home.removeAttribute('data-home-input');
      schedule();
    };
    listen(window, 'wheel', pointerInput, { passive: true });
    listen(window, 'touchstart', pointerInput, { passive: true });
    listen(home, 'pointerdown', pointerInput, { passive: true });
    listen(window, 'scroll', schedule, { passive: true });
    listen(window, 'resize', measure, { passive: true });
    listen(window, 'pageshow', measure);
    listen(document, 'visibilitychange', () => {
      home.toggleAttribute('data-home-idle', document.hidden);
      if (document.hidden && frame) { cancelAnimationFrame(frame); frame = 0; }
      else schedule();
    });
    listen(reduceMotion, 'change', syncMotion);
    listen(wideScreen, 'change', schedule);
    if (toggle) listen(toggle, 'click', () => { paused = !paused; syncMotion(); });
    chapters.forEach(({ link, target }) => {
      target.setAttribute('tabindex', '-1');
      listen(link, 'click', (event) => {
        if (event.button || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        event.preventDefault();
        const instant = event.detail === 0 || !motionOn();
        if (instant) showEverything();
        target.scrollIntoView({ behavior: instant ? 'instant' : 'smooth', block: 'start' });
        target.focus({ preventScroll: true });
        window.history.replaceState(window.history.state, '', link.hash);
      });
    });
    if ('ResizeObserver' in window) {
      const sizeObserver = new ResizeObserver(measure);
      sizeObserver.observe(home);
      if (header) sizeObserver.observe(header);
      if (nav) sizeObserver.observe(nav);
      cleanups.push(() => sizeObserver.disconnect());
    }
    document.fonts?.ready.then(() => { if (!destroyed) measure(); });

    const modelButtons = [...home.querySelectorAll('[data-model-button]')];
    const modelImage = home.querySelector('[data-model-image]');
    const modelFields = { name: 'name', code: 'code', speed: 'speed', air: 'air', weight: 'weight' };
    modelButtons.forEach((button) => listen(button, 'click', (event) => {
      modelButtons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      if (swapTimer) window.clearTimeout(swapTimer);
      const swap = () => {
        if (!modelImage || !button.dataset.render) return;
        modelImage.src = button.dataset.render;
        modelImage.alt = button.dataset.alt || '';
        modelImage.classList.remove('is-swapping');
      };
      if (modelImage && motionOn() && event.detail !== 0) {
        modelImage.classList.add('is-swapping');
        swapTimer = window.setTimeout(swap, 160);
      } else swap();
      Object.entries(modelFields).forEach(([selector, fieldName]) => {
        const output = home.querySelector(`[data-model-${selector}]`);
        if (output) output.textContent = button.dataset[fieldName] || '';
      });
    }));

  const previewAddForm = home.querySelector('[data-preview-add-form]');
  if (previewAddForm) {
    const addButton = previewAddForm.querySelector('button[type="submit"]');
    const status = previewAddForm.querySelector('[data-preview-add-status]');
    const quantity = previewAddForm.querySelector('input[type="number"]');
    const minus = previewAddForm.querySelector('button[aria-label*="Decrease"]');
    const plus = previewAddForm.querySelector('button[aria-label*="Increase"]');
    const initialButtonText = addButton ? addButton.innerHTML : '';
    const initialStatusText = status ? status.textContent : '';
    const updateQuantity = (delta) => {
      if (!quantity) return;
      quantity.value = String(Math.max(1, Number(quantity.value || 1) + delta));
    };
    if (minus) listen(minus, 'click', () => updateQuantity(-1));
    if (plus) listen(plus, 'click', () => updateQuantity(1));
    listen(previewAddForm, 'submit', (event) => {
      event.preventDefault();
      if (!addButton) return;
      addButton.innerHTML = 'Added to lineup ✓';
      addButton.classList.add('is-added');
      if (status) status.textContent = '6400L reserved in your lineup · continue to secure checkout.';
      if (previewTimer) window.clearTimeout(previewTimer);
      previewTimer = window.setTimeout(() => {
        addButton.innerHTML = initialButtonText;
        addButton.classList.remove('is-added');
        if (status) status.textContent = initialStatusText;
      }, 3200);
    });
  }
    measure();
    controllers.set(home, () => {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      if (swapTimer) window.clearTimeout(swapTimer);
      if (previewTimer) window.clearTimeout(previewTimer);
      cleanups.forEach((cleanup) => cleanup());
      showEverything();
      depth.forEach(({ image }) => image.style.removeProperty('transform'));
      loops.forEach((item) => item.classList.remove('home-in-view'));
      home.removeAttribute('data-home-motion');
      home.removeAttribute('data-home-input');
      home.removeAttribute('data-home-idle');
      controllers.delete(home);
    });
  }

  function mount(scope) {
    if (scope.matches?.('[data-home-scroll]')) init(scope);
    scope.querySelectorAll('[data-home-scroll]').forEach(init);
  }
  mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    [...controllers].forEach(([home, destroy]) => { if (event.target.contains(home)) destroy(); });
  });
})();
